import { useState } from 'react'
import { Box, Typography, useTheme, TextField, Button, Paper, IconButton } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { joinAbsRoute } from 'src/common/utils'
import { HelperWrapper, MainContainer } from 'src/components'
import { ROUTES } from 'src/constants'
import { ETranslationNs } from 'src/types'
import { useDecryptTxData, useEncryptTxData } from 'src/hooks/admin-tools'
import { useDeleteFakeConsumerScans } from 'src/hooks/admin-tools/use-delete-fake-consumer-scans.hook'
import { useGetFaceScansInfo } from 'src/hooks/admin-tools/use-get-fake-scans-info.hook'
import { DeleteFakeScansModal } from 'src/components/admin-tools/delete-fake-scans-modal'

const AdminToolsScreen = () => {
  const theme = useTheme()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [rawJson, setRawJson] = useState<string>('')
  const [encryptedHex, setEncryptedHex] = useState<string>('')

  const {
    mutate: encryptData,
    data: encryptDataResp,
    isLoading: isEncryptDataLoading,
    isSuccess: isEncryptDataSuccess,
  } = useEncryptTxData()
  const {
    mutate: decryptData,
    data: decryptDataResp,
    isLoading: isDecryptDataLoading,
    isSuccess: isDecryptDataSuccess,
  } = useDecryptTxData()

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const {
    mutate: deleteFakeScans,
    data: deleteFakeScansResp,
    isLoading: isDeleteFakeScansLoading,
  } = useDeleteFakeConsumerScans()

  const handleClose = () => {
    setIsModalOpen(false)
  }

  const handleDeletConsumerScans = () => {
    deleteFakeScans()
  }

  return (
    <MainContainer
      headerTitle={<HelperWrapper t="admin-tools-header-title" ns={ETranslationNs.Navbar} />}
      navigateBack
      navigateBackRoute={joinAbsRoute(ROUTES.ADMIN_ROUTES)}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(4),
          p: theme.spacing(4),
          maxWidth: 800,
        }}
      >
        {/* Encrypt Section */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ pb: '15px' }}>
            🔐 Encrypt Transaction Data
          </Typography>
          <TextField
            label="JSON Payload"
            placeholder='e.g. {"foo": 123}'
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
          />
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => encryptData(rawJson)}
            disabled={isEncryptDataLoading}
          >
            {isEncryptDataLoading ? 'Encrypting…' : 'Encrypt'}
          </Button>

          {isEncryptDataSuccess && encryptDataResp && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, position: 'relative' }}>
              <Typography variant="subtitle2">Encrypted Hex:</Typography>
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8 }}
                onClick={() => copyToClipboard(encryptDataResp)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <Typography
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowY: 'auto',
                  maxHeight: 200,
                  mt: 1,
                }}
              >
                {encryptDataResp}
              </Typography>
            </Paper>
          )}
        </Paper>

        {/* Decrypt Section */}
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ pb: '15px' }}>
            🔓 Decrypt Transaction Data
          </Typography>
          <TextField
            label="Encrypted Hex"
            placeholder="0x…"
            fullWidth
            multiline
            minRows={3}
            maxRows={10}
            value={encryptedHex}
            onChange={(e) => setEncryptedHex(e.target.value)}
          />
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => decryptData(encryptedHex)}
            disabled={isDecryptDataLoading}
          >
            {isDecryptDataLoading ? 'Decrypting…' : 'Decrypt'}
          </Button>

          {isDecryptDataSuccess && decryptDataResp && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, position: 'relative' }}>
              <Typography variant="subtitle2">Decrypted JSON:</Typography>
              <IconButton
                size="small"
                sx={{ position: 'absolute', top: 8, right: 8 }}
                onClick={() => copyToClipboard(JSON.stringify(decryptDataResp, null, 2))}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
              <Typography
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowY: 'auto',
                  maxHeight: 350,
                  mt: 1,
                }}
              >
                {JSON.stringify(decryptDataResp, null, 2)}
              </Typography>
            </Paper>
          )}
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ pb: '15px' }}>
              ❌ Delete Fake Consumer Scans
            </Typography>

            <Typography variant="body2" sx={{ pb: '3px' }}>
              This action will permanently delete all PENDING scans flagged as fake based on the
              following criteria:
            </Typography>

            <Typography variant="caption" sx={{ pl: 2, pb: '5px' }}>
              • Scans belonging to suspended users
              <br />
              • Scans with product type 'unknown'
              <br />
              • Scans missing location data or with empty scanner info
              <br />• Scans without media
            </Typography>

            <Typography variant="body2" sx={{ pb: '3px' }}>
              After deletion, a summary log will be created detailing:
            </Typography>

            <Typography variant="caption" sx={{ pl: 2, pb: '15px' }}>
              • Total number of deleted scans
              <br />
              • Breakdown of deletions by user (user ID and email)
              <br />• Any errors encountered during batch deletion
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', pb: '10px' }}>
              You can review this summary in the logs by searching for:{' '}
              <b>"Fake Consumer Scans Deletion summary"</b>
            </Typography>

            <Button
              sx={{ width: 'fit-content' }}
              variant="contained"
              onClick={() => setIsModalOpen(true)}
              disabled={isDeleteFakeScansLoading}
              color="warning"
            >
              {isDeleteFakeScansLoading ? 'Deleting…' : 'Delete'}
            </Button>
          </Box>
        </Paper>
      </Box>

      <DeleteFakeScansModal
        handleClose={handleClose}
        open={isModalOpen}
        handleDelete={handleDeletConsumerScans}
        isLoading={isDeleteFakeScansLoading}
      />
    </MainContainer>
  )
}

export default AdminToolsScreen
